import { Fragment, forwardRef, useContext, useRef, useState } from 'react';

import { Loader } from 'Components/Loader';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { UploadFileButton } from './UploadFileButton';
import { Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { DeleteModal } from 'Components';
import { RequestsContext } from 'Context';
import { FileActions } from './FileActions';
import { MAX_FILE_SIZE_IN_MB } from 'Config';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PlusBtn, UploadDraggingFile } from 'Images';
import { isFileValid } from 'Utils/isFileValid';
import { getCleanFileName } from 'Utils';

export interface IFileUpload {
  loading?: boolean;
  dataRequired?: boolean;
  dataRequiredWith?: number[];
  fieldIndex: number;
  fieldTitle?: string;
  fieldId?: string;
}

export const FileUpload = forwardRef<HTMLButtonElement, IFileUpload>(
  (props, ref) => {
    const { fieldIndex, fieldId } = props;
    const { requestsData, setRequestsData } = useContext(RequestsContext);
    const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
    const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
    const [isLoading] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [currFileName, setCurrFileName] = useState<string>('');
    const [errorMessage, setErrorMsg] = useState(
      generalDictionary.fileErrorNew
    );
    const [fileDraggingIndex, setFileDraggingIndex] = useState<number | null>(
      null
    );
    const [showUploadArea, setShowUploadArea] = useState(true);

    const checkFileSize = (file: File) => {
      if (file.size < 1000) {
        return false;
      }
      const MB = file.size / 1024 / 1024;
      return MB < MAX_FILE_SIZE_IN_MB;
    };

    const checkTotalFileSize = (selectedFile?: File) => {
      let totalSize = 0;
      requestsData.fieldAttachmentsData.forEach((field) => {
        Object.values(field.attachments).forEach((file) => {
          totalSize += (file.size + (selectedFile?.size || 0)) / 1024 / 1024;
        });
      });

      return totalSize < MAX_FILE_SIZE_IN_MB;
    };

    const checkExtension = (file: File) => {
      const allowsExtension = [
        'doc',
        'docx',
        'jpg',
        'png',
        'pdf',
        'tif',
        'tiff',
        'jpeg',
      ];
      const fileExt = file.name.split('.').pop();
      if (fileExt && allowsExtension.includes(fileExt.toLowerCase())) {
        return true;
      }
      return false;
    };

    const onChangeHandler = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = event.target.files;
      if (file !== null) {
        if (checkFileSize(file[0]) && checkExtension(file[0])) {
          if (!checkTotalFileSize(file[0])) {
            setErrorMsg(generalDictionary.totalFileSizeError);
            setShowError(true);
            return;
          } else setErrorMsg(generalDictionary.fileErrorNew);

          if (!(await isFileValid(file[0]))) {
            setErrorMsg(generalDictionary.invalidFileError);
            setShowError(true);
            return;
          }

          const fileWithoutExtension = file[0].name.split('.');
          const cleanFileName = fileWithoutExtension[0].replace(
            /[^a-zA-Z0-9\u05D0-\u05EA\- _]/g,
            ''
          );
          const fileExt = file[0].name.split('.').pop();
          const fileName = cleanFileName + '.' + fileExt;

          setRequestsData((prevRequestData) => {
            const clonePrevRequestData = { ...prevRequestData };
            if (typeof fieldIndex === 'number') {
              clonePrevRequestData.fieldAttachmentsData[fieldIndex] = {
                fieldId: String(fieldId) || '0',
                attachments: {
                  ...prevRequestData.fieldAttachmentsData[fieldIndex]
                    ?.attachments,
                  [fileName]: file[0],
                },
              };
            }
            return clonePrevRequestData;
          });
          setShowUploadArea(false);
          setShowError(false);
          return;
        } else setErrorMsg(generalDictionary.fileErrorNew);
        setShowError(true);
      }
    };

    const fileInput = useRef<HTMLInputElement | null>(null);

    const deleteFileHandler = async (fileName: string) => {
      setCurrFileName(fileName);
      setShowDeleteModal(true);
    };

    const deleteModal = async (fileName: string) => {
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        delete clonePrevRequestData.fieldAttachmentsData[fieldIndex]
          .attachments[fileName];

        return clonePrevRequestData;
      });

      (fileInput.current as any).value = null;

      if (
        Object.keys(requestsData.fieldAttachmentsData[fieldIndex].attachments)
          .length === 0
      )
        setShowUploadArea(true);

      closeDeleteModalHandler();

      setTimeout(
        () =>
          (
            document.querySelector(
              `button[data-id="${
                requestsData.fieldAttachmentsData[props.fieldIndex].fieldId
              }"]`
            ) as any
          ).focus(),
        0
      );
    };

    const closeDeleteModalHandler = () => {
      setCurrFileName('');
      setShowDeleteModal(false);
    };

    const watchFile = async (fileData: File, fileName: string) => {
      if (fileData) window.open(URL.createObjectURL(fileData));
    };

    const clickFileInput = () => {
      if (fileInput.current) {
        fileInput.current.click();
      }
    };

    const isDesktop = useIsDesktop();

    const handleFiles = async (files: FileList, currentFieldIndex: number) => {
      if (checkFileSize(files[0]) && checkExtension(files[0])) {
        if (!checkTotalFileSize(files[0])) {
          setErrorMsg(generalDictionary.totalFileSizeError);
          return;
        } else setErrorMsg(generalDictionary.fileErrorNew);

        if (!(await isFileValid(files[0]))) {
          setErrorMsg(generalDictionary.invalidFileError);
          setShowError(true);
          return;
        }

        const fileName = getCleanFileName(files[0].name);
        setRequestsData((prevRequestData) => {
          const clonePrevRequestData = { ...prevRequestData };
          clonePrevRequestData.fieldAttachmentsData[currentFieldIndex] = {
            fieldId: fieldId || '',
            attachments: {
              ...prevRequestData.fieldAttachmentsData[currentFieldIndex]
                ?.attachments,
              [fileName]: files[0],
            },
          };
          return clonePrevRequestData;
        });
        setShowError(false);
        return;
      } else setErrorMsg(generalDictionary.fileErrorNew);
      setShowError(true);
    };

    const handleOnDrop = (
      event: React.DragEvent<HTMLDivElement>,
      index: number
    ) => {
      event.preventDefault();
      const droppedFiles = event.dataTransfer.files;
      handleFiles(droppedFiles, index);
      setFileDraggingIndex(null);
    };

    const handleDragEnter = (
      event: React.DragEvent<HTMLDivElement>,
      index: number
    ) => {
      event.preventDefault();
      if (fileDraggingIndex !== index) setFileDraggingIndex(index);
    };

    const handleDragLeave = (
      event: React.DragEvent<HTMLDivElement>,
      index: number
    ) => {
      event.preventDefault();
      setFileDraggingIndex(null);
    };

    const uploadArea = isDesktop ? (
      <Box
        sx={{
          border:
            fileDraggingIndex === fieldIndex
              ? '2px dashed #A020FF'
              : '1px solid #B7CCFF',
          borderColor: showError ? '#ec0000' : undefined,
          padding: '24px',
          background: '#FBFCFF',
          borderRadius: '8px',
          textAlign: 'center',
          mt: 2,
          position: 'relative',
        }}
        aria-invalid={showError}
        tabIndex={!fieldId ? -1 : 0}
      >
        <Box
          onDragOver={(event) => event.preventDefault()}
          onDrop={(e) => handleOnDrop(e, fieldIndex)}
          onDragEnter={(e) => handleDragEnter(e, fieldIndex)}
          onDragLeave={(e) => handleDragLeave(e, fieldIndex)}
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            bgcolor: 'transparent',
            top: 0,
            left: 0,
            zIndex: fileDraggingIndex === fieldIndex ? 1 : undefined,
          }}
        />
        {fileDraggingIndex === fieldIndex ? (
          <Stack padding='18px' m='-1px 0'>
            <Avatar
              src={UploadDraggingFile}
              alt=''
              sx={{
                width: '48px',
                height: '48px',
                margin: '12px auto',
              }}
            />
            <Typography
              sx={{
                pointerEvents: 'none',
                color: '#344266',
              }}
              variant='body6'
            >
              {dictionary.uploadFile.dragFilesHere}
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography
              sx={{
                pointerEvents: 'none',
                color: '#344266',
              }}
              variant='body3'
            >
              {dictionary.uploadFile.dragFilesHere}
            </Typography>
            <Divider
              aria-hidden='true'
              sx={{ margin: '20px 0', pointerEvents: 'none' }}
            >
              {dictionary.uploadFile.or}
            </Divider>
            <input
              type='file'
              accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
              hidden
              onChange={(e) => onChangeHandler(e)}
              id={`upload-file-${fieldIndex}`}
            />
            <label htmlFor={`upload-file-${fieldIndex}`} className='sr-only'>
              {`${dictionary.uploadFile.chooseFiles}`}
            </label>

            <Box width={'186px'} m={'0 auto'}>
              <UploadFileButton
                dataRequired={props.dataRequired}
                dataRequiredWith={props.dataRequiredWith}
                clickHandler={() => clickFileInput()}
                ref={null}
                ariaLabel=''
                dataId={fieldId}
              >
                {dictionary.uploadFile.chooseFiles}
              </UploadFileButton>
            </Box>
          </>
        )}
      </Box>
    ) : (
      <UploadFileButton
        dataRequired={props.dataRequired}
        dataRequiredWith={props.dataRequiredWith}
        clickHandler={clickFileInput}
        ref={ref}
        ariaLabel={props.fieldTitle}
        dataId={fieldId}
      >
        {generalDictionary.fileUploadLabel}
      </UploadFileButton>
    );

    return (
      <>
        <input
          ref={(e) => {
            fileInput.current = e;
          }}
          type='file'
          accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
          hidden
          onChange={onChangeHandler}
          id={`upload-file-${fieldIndex}`}
        />
        <label htmlFor={`upload-file-${fieldIndex}`} className='sr-only'>
          {`${generalDictionary.fileUploadLabel} ${props.fieldTitle}`}
        </label>
        {showDeleteModal && currFileName && (
          <DeleteModal
            isVisible={showDeleteModal}
            closeModal={closeDeleteModalHandler}
            deleteFile={() => deleteModal(currFileName)}
            fileName={currFileName}
          />
        )}

        {requestsData.fieldAttachmentsData &&
          Object.keys(
            requestsData.fieldAttachmentsData[props.fieldIndex]?.attachments ||
              {}
          ).length > 0 && (
            <>
              <Stack gap={'16px'}>
                {requestsData.fieldAttachmentsData[props.fieldIndex]
                  ?.attachments &&
                  Object.entries(
                    requestsData.fieldAttachmentsData[props.fieldIndex]
                      .attachments
                  ).map((field, index, arr) => {
                    return (
                      <Fragment key={field[0]}>
                        <FileActions
                          key={index}
                          filename={field[0]}
                          watchFile={() => watchFile(field[1], field[0])}
                          deleteFile={() => deleteFileHandler(field[0])}
                        />
                        {index === arr.length - 1 && (
                          <Button
                            data-id={fieldId}
                            data-required={false}
                            sx={{
                              position: isDesktop ? 'relative' : undefined,
                              color: 'primary.main',
                              fontSize: isDesktop ? '18px' : '16px',
                              width: isDesktop ? '170px' : '170px',
                              mt: isDesktop ? '-40px' : '-72px',
                              mb: isDesktop ? undefined : '20px',
                              height: '32px',
                              padding: isDesktop ? undefined : '0',
                              '&:focus': {
                                outline: isDesktop ? undefined : 'none',
                              },
                            }}
                            startIcon={
                              <Avatar
                                alt=''
                                src={PlusBtn}
                                sx={{ width: '13px', height: '13px' }}
                              />
                            }
                            onClick={() => {
                              setShowUploadArea(!showUploadArea);
                            }}
                          >
                            {dictionary.successDialog.uploadMoreFile}
                          </Button>
                        )}
                      </Fragment>
                    );
                  })}
              </Stack>
            </>
          )}

        {showUploadArea && uploadArea}

        {showError ? (
          <Typography
            color='error'
            fontSize='14px'
            textAlign='left'
            maxWidth='310px'
            p='10px'
            role={Boolean(showError) ? 'alert' : undefined}
          >
            {errorMessage}
          </Typography>
        ) : null}
        {isLoading && <Loader />}
      </>
    );
  }
);
